import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  FirestoreDataConverter,
  DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';
import { monitor } from './monitoring';
import type { ServiceCategory, Provider, ServiceOffering, WeeklySchedule } from '../types';

// db is provided by src/lib/firebase with test-safe stubs when running under Vitest

/** Remove any undefined values recursively (Firestore does not allow undefined). */
function stripUndefined<T>(val: T): T {
  // Leave null and primitives as-is (except undefined which is handled below)
  if (val === null) return val as any;

  if (Array.isArray(val)) {
    const mapped = (val as unknown[]).map((v) => stripUndefined(v as any));
    return mapped.filter((v) => v !== undefined) as any;
  }

  if (typeof val === 'object') {
    // Keep non-plain objects as-is (Firestore FieldValue, Timestamp, Date, etc.)
    const proto = Object.getPrototypeOf(val as object);
    const isPlain = proto === Object.prototype || proto === null;
    if (!isPlain) return val as any;

    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(val as Record<string, any>)) {
      const sv = stripUndefined(v);
      if (sv !== undefined) out[k] = sv;
    }
    return out as any;
  }

  // If it's undefined, return undefined (caller will drop it); otherwise return original
  return (val ?? undefined) as any;
}

// ──────────────────────────────────────────────────────────────
// Validation helpers for Provider converter
// ──────────────────────────────────────────────────────────────
const isObj = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === 'string';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isArr = (v: unknown): v is any[] => Array.isArray(v);
const isTimestampLike = (v: unknown): boolean => !!(v && typeof (v as any).toDate === 'function');

function safeStatus(v: unknown): Provider['status'] {
  return v === 'verified' || v === 'rejected' || v === 'suspended' || v === 'pending'
    ? (v as Provider['status'])
    : 'pending';
}

function safeOnboardingStep(v: unknown): Provider['onboardingStep'] {
  return v === 'signup' ||
    v === 'verification' ||
    v === 'profile' ||
    v === 'availability' ||
    v === 'complete'
    ? (v as Provider['onboardingStep'])
    : 'signup';
}

function safeCreatedAt(v: unknown): any {
  return isTimestampLike(v) || v instanceof Date ? v : new Date();
}

function safeUpdatedAt(v: unknown): any {
  return isTimestampLike(v) || v instanceof Date ? v : undefined;
}

function normalizePersonalInfo(d: unknown): Provider['personalInfo'] {
  const p = isObj(d) ? d : {};
  return {
    firstName: isStr(p.firstName) ? p.firstName : '',
    lastName: isStr(p.lastName) ? p.lastName : '',
    email: isStr(p.email) ? p.email : '',
    phone: isStr(p.phone) ? p.phone : '',
    dateOfBirth: isStr(p.dateOfBirth) ? p.dateOfBirth : '',
    nationalId: isStr(p.nationalId) ? p.nationalId : '',
  };
}

function normalizeDocuments(d: unknown, fallbackDate: any): Provider['documents'] {
  const docs = isObj(d) ? d : {};
  return {
    nationalIdFront: isStr(docs.nationalIdFront) ? docs.nationalIdFront : '',
    nationalIdBack: isStr(docs.nationalIdBack) ? docs.nationalIdBack : '',
    nonCriminalCertificate: isStr(docs.nonCriminalCertificate) ? docs.nonCriminalCertificate : '',
    certifications: isArr(docs.certifications) ? docs.certifications.filter(isStr) : [],
    submittedAt:
      isTimestampLike(docs.submittedAt) || docs.submittedAt instanceof Date
        ? docs.submittedAt
        : fallbackDate,
    reviewedAt:
      isTimestampLike(docs.reviewedAt) || docs.reviewedAt instanceof Date
        ? docs.reviewedAt
        : undefined,
    rejectionReason: isStr(docs.rejectionReason) ? docs.rejectionReason : undefined,
  } as Provider['documents'];
}

function normalizeServices(arr: unknown): ServiceOffering[] {
  if (!isArr(arr)) return [];
  return arr.filter(isObj).map((s) => ({
    categoryId: isStr(s.categoryId) ? s.categoryId : '',
    pricingModel: s?.pricingModel === 'hourly' ? 'hourly' : 'fixed',
    price: isNum(s.price) ? s.price : 0,
    description: isStr(s.description) ? s.description : '',
    estimatedDuration: isNum(s.estimatedDuration) ? s.estimatedDuration : 0,
  }));
}

function normalizeProfile(d: unknown): Provider['profile'] {
  const pr = isObj(d) ? d : {};
  return {
    bio: isStr(pr.bio) ? pr.bio : '',
    profilePicture: isStr(pr.profilePicture) ? pr.profilePicture : '',
    services: normalizeServices(pr.services),
    portfolio: isArr(pr.portfolio) ? pr.portfolio.filter(isStr) : [],
    yearsOfExperience: isNum(pr.yearsOfExperience) ? pr.yearsOfExperience : 0,
  };
}

function normalizeAvailability(d: unknown): Provider['availability'] {
  const av = isObj(d) ? d : {};
  const workingHours: WeeklySchedule = isObj(av.workingHours)
    ? av.workingHours
    : defaultWeeklyHours();
  const serviceCenter =
    isObj(av.serviceCenter) && isNum(av.serviceCenter.lat) && isNum(av.serviceCenter.lng)
      ? { lat: av.serviceCenter.lat, lng: av.serviceCenter.lng }
      : undefined;
  const exceptions = isArr(av.exceptions) ? av.exceptions : [];
  return {
    isAvailable: isBool(av.isAvailable) ? av.isAvailable : false,
    workingHours,
    serviceRadius: isNum(av.serviceRadius) ? av.serviceRadius : 10,
    serviceCenter,
    exceptions,
  } as Provider['availability'];
}

function normalizeStats(d: unknown): Provider['stats'] {
  const st = isObj(d) ? d : {};
  return {
    totalBookings: isNum(st.totalBookings) ? st.totalBookings : 0,
    completedBookings: isNum(st.completedBookings) ? st.completedBookings : 0,
    pendingBookings: isNum(st.pendingBookings) ? st.pendingBookings : 0,
    cancelledBookings: isNum(st.cancelledBookings) ? st.cancelledBookings : 0,
    totalEarnings: isNum(st.totalEarnings) ? st.totalEarnings : 0,
    averageRating: isNum(st.averageRating) ? st.averageRating : 0,
  };
}

function safeDefaultProvider(id: string): Provider {
  return {
    id,
    userId: id,
    status: 'pending',
    onboardingStep: 'signup',
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      nationalId: '',
    },
    documents: {
      nationalIdFront: '',
      nationalIdBack: '',
      nonCriminalCertificate: '',
      certifications: [],
      submittedAt: new Date(),
    },
    profile: {
      bio: '',
      profilePicture: '',
      services: [],
      portfolio: [],
      yearsOfExperience: 0,
    },
    availability: {
      isAvailable: false,
      workingHours: defaultWeeklyHours(),
      serviceRadius: 10,
      exceptions: [],
    },
    stats: {
      totalBookings: 0,
      completedBookings: 0,
      pendingBookings: 0,
      cancelledBookings: 0,
      totalEarnings: 0,
      averageRating: 0,
    },
    categoryId: '',
    details: {},
    createdAt: new Date(),
    updatedAt: undefined,
  } as Provider;
}

/** ──────────────────────────────────────────────────────────────
 * Service Categories (read-only)
 * ────────────────────────────────────────────────────────────── */
const categoryConverter: FirestoreDataConverter<ServiceCategory> = {
  toFirestore: (c: ServiceCategory): DocumentData => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...rest } = c;
    // Field 'id' intentionally omitted from Firestore document; no need for 'void' operator (Sonar S3735)
    return rest as DocumentData;
  },
  fromFirestore: (snap): ServiceCategory => {
    const d = snap.data() as any;
    return {
      id: snap.id,
      name: d?.name ?? '',
      description: d?.description ?? '',
      // Only imageUrl is used now; icon removed from domain model
      imageUrl: d?.imageUrl ?? '',
      tradeType: d?.tradeType ?? 'general',
      archived: !!d?.archived,
      createdAt: d?.createdAt ?? '',
      fields: Array.isArray(d?.fields) ? d.fields : [],
    };
  },
};

export async function getAllCategories(): Promise<ServiceCategory[]> {
  const col = collection(db, 'serviceCategories').withConverter(categoryConverter);
  const q = query(col, orderBy('name'));
  const snap = await getDocs(q);
  const results = snap.docs.map((d) => d.data());
  // Diagnostic logging – remove once categories load correctly
  // eslint-disable-next-line no-console
  console.log('[getAllCategories] docs returned:', snap.size);
  // eslint-disable-next-line no-console
  results.forEach((c) => console.log(`  [category] "${c.name}" imageUrl="${c.imageUrl}"`));
  return results;
}

/** ──────────────────────────────────────────────────────────────
 * Provider helpers (create/read/update)
 * Documents live at /providers/{uid}
 * ────────────────────────────────────────────────────────────── */
export async function getProviderByUid(uid: string): Promise<Provider | null> {
  const ref = doc(db, 'providers', uid).withConverter(providerConverter);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/** Provider converter for typed reads/writes */
export const providerConverter: FirestoreDataConverter<Provider> = {
  toFirestore: (p: Provider): DocumentData => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...rest } = p;
    // 'id' intentionally excluded; removed 'void id' (S3735)
    return rest as DocumentData;
  },
  fromFirestore: (snap): Provider => {
    const d = snap.data() as any;
    try {
      const status = safeStatus(d?.status);
      const onboardingStep = safeOnboardingStep(d?.onboardingStep);
      const userId = isStr(d?.userId) ? d.userId : snap.id;
      const categoryId = isStr(d?.categoryId) ? d.categoryId : '';

      const createdAt = safeCreatedAt(d?.createdAt);
      const updatedAt = safeUpdatedAt(d?.updatedAt);

      const personalInfo = normalizePersonalInfo(d?.personalInfo);
      const documents = normalizeDocuments(d?.documents, createdAt);
      const profile = normalizeProfile(d?.profile);
      const availability = normalizeAvailability(d?.availability);
      const stats = normalizeStats(d?.stats);
      const details = isObj(d?.details) ? d.details : {};

      return {
        id: snap.id,
        userId,
        status,
        onboardingStep,
        personalInfo,
        documents,
        profile,
        availability,
        stats,
        categoryId,
        details,
        createdAt,
        updatedAt,
      } satisfies Provider; // Remove unnecessary assertion (S4325)
    } catch (err) {
      // Last-resort safe default to avoid runtime crashes; also log for observability
      // Redact PII by hashing the user ID to avoid logging raw UIDs
      const redactedId = snap.id
        ? `uid_${snap.id.substring(0, 4)}...${snap.id.substring(snap.id.length - 4)}`
        : 'unknown';
      monitor.error('providerConverter.fromFirestore: invalid provider document', {
        redactedId,
        error: err,
      });
      return safeDefaultProvider(snap.id);
    }
  },
};

/** Create skeleton after sign-up */
export async function createProviderSkeleton(
  uid: string,
  partial: Partial<Provider>,
): Promise<Provider> {
  const ref = doc(db, 'providers', uid);
  const now = serverTimestamp();

  // Build nested objects without spreading a useless empty object
  const personalInfo = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationalId: '',
  } as Provider['personalInfo'];
  if (partial.personalInfo) Object.assign(personalInfo, partial.personalInfo);

  const documents = {
    nationalIdFront: '',
    nationalIdBack: '',
    nonCriminalCertificate: '',
    certifications: [],
    submittedAt: now as any,
  } as Provider['documents'];
  if (partial.documents) Object.assign(documents, partial.documents);

  const profileObj = {
    bio: '',
    profilePicture: '',
    services: [] as ServiceOffering[],
    portfolio: [],
    yearsOfExperience: 0,
  } as Provider['profile'];
  if (partial.profile) Object.assign(profileObj, partial.profile);

  const availabilityObj = {
    isAvailable: false,
    workingHours: defaultWeeklyHours(),
    serviceRadius: 10,
    exceptions: [],
  } as Provider['availability'];
  if (partial.availability) Object.assign(availabilityObj, partial.availability);

  const statsObj = {
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    totalEarnings: 0,
    averageRating: 0,
  } as Provider['stats'];
  if (partial.stats) Object.assign(statsObj, partial.stats);

  const base: Provider = {
    id: uid,
    userId: uid,

    // ✅ Single unified status
    status: partial.status ?? 'pending',
    onboardingStep: partial.onboardingStep ?? 'signup',

    personalInfo,

    documents,

    profile: profileObj,

    availability: availabilityObj,

    stats: statsObj,

    categoryId: partial.categoryId ?? '',
    details: partial.details ?? {},

    createdAt: now as any,
    updatedAt: now as any,
  };

  // Ensure no undefined are sent to Firestore
  await setDoc(ref, stripUndefined(base), { merge: true });
  // Fetch the persisted document so timestamps are resolved (no FieldValue placeholders)
  const saved = await getProviderByUid(uid);
  if (!saved) {
    throw new Error('Failed to create provider skeleton');
  }
  return saved;
}

/** Update any provider fields (shallow merge) */
export async function updateProvider(uid: string, patch: Partial<Provider> | Record<string, any>) {
  const ref = doc(db, 'providers', uid);

  // Flatten nested objects into Firestore dot-notation to avoid overwriting entire subtrees
  const flatten = (obj: Record<string, any>, prefix = ''): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(obj)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v === undefined) continue;
      if (v && typeof v === 'object') {
        const proto = Object.getPrototypeOf(v);
        const isPlain = proto === Object.prototype || proto === null;
        if (isPlain) {
          Object.assign(out, flatten(v as Record<string, any>, key));
        } else {
          out[key] = v; // Firestore FieldValue/Timestamp/Date, etc.
        }
      } else {
        out[key] = v;
      }
    }
    return out;
  };

  const withTimestamp = { ...patch, updatedAt: serverTimestamp() } as Record<string, any>;
  const flattenedInput: Record<string, any> = stripUndefined(withTimestamp); // Typed without assertion (S4325)
  const flattened = flatten(flattenedInput);
  await updateDoc(ref, flattened);
}

/** Submit verification documents */
export async function submitVerification(
  uid: string,
  payload: {
    nationalIdFront: string;
    nationalIdBack: string;
    nonCriminalCertificate: string;
    certifications?: string[];
  },
) {
  await updateProvider(uid, {
    'documents.nationalIdFront': payload.nationalIdFront,
    'documents.nationalIdBack': payload.nationalIdBack,
    'documents.nonCriminalCertificate': payload.nonCriminalCertificate,
    'documents.certifications': payload.certifications ?? [],
    'documents.submittedAt': serverTimestamp() as any,
    onboardingStep: 'verification',
  });
}

/** Mark provider as verified (admin approval) */
export async function adminApproveProvider(uid: string) {
  const ref = doc(db, 'providers', uid);
  await updateDoc(ref, {
    status: 'verified',
    'documents.reviewedAt': serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Reject a provider (with reason) */
export async function adminRejectProvider(uid: string, reason: string) {
  const ref = doc(db, 'providers', uid);
  await updateDoc(ref, {
    status: 'rejected',
    'documents.rejectionReason': reason,
    'documents.reviewedAt': serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Suspend a verified provider */
export async function adminSuspendProvider(uid: string) {
  const ref = doc(db, 'providers', uid);
  await updateDoc(ref, {
    status: 'suspended',
    updatedAt: serverTimestamp(),
  });
}

/** Permanently delete a provider document.
 *  NOTE: Does not cascade delete any potential subcollections.
 *  If subcollections exist (e.g., bookings), a Cloud Function or recursive cleanup is needed.
 */
export async function deleteProvider(uid: string) {
  const ref = doc(db, 'providers', uid);
  await deleteDoc(ref);
}

/** Default weekly hours helper */
function defaultWeeklyHours(): WeeklySchedule {
  return {
    monday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    tuesday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    wednesday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    thursday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    friday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    saturday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    sunday: { enabled: false, startTime: '09:00', endTime: '17:00' },
  };
}
